import jsPDF from "jspdf";
import { drawProHeader, getSettingsCached, loadSignatureDataUrl } from "./pdfHeader";

/**
 * Informe médico de consulta — cabezal ECG estándar + formato clásico (grid table + secciones libres).
 * Reproduce fielmente el formato solicitado por el Dr. Dimy Laguerre.
 * Añade firma electrónica eIDAS (HMAC) y QR de verificación pública.
 */
export async function generateMedicalReportPDF(rep, opts = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const margin = 40;
  const settings = rep._settings || (await getSettingsCached());
  const doctor = rep._doctor || {
    name: rep.doctor_name,
    specialty: rep.doctor_specialty,
    license_number: rep.doctor_license,
    college: rep.doctor_college,
  };

  // Cabezal estándar ECG (mismo que recetas, certificados, interrogatorios)
  const headerH = await drawProHeader(doc, {
    docTitle: "INFORME MÉDICO DE CONSULTA",
    docSubtitle: rep.report_date ? `Fecha: ${new Date(rep.report_date).toLocaleDateString("es-ES")}` : "",
    settings, doctor,
  });
  let y = headerH + 12;

  // ---------- Encabezado tipo "grid" ----------
  // Colores/estilos
  const cellFill = [242, 244, 247];  // gris muy claro para etiquetas
  const border = [15, 23, 42];       // negro para líneas
  const text = [15, 23, 42];

  doc.setDrawColor(border[0], border[1], border[2]);
  doc.setLineWidth(0.8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(text[0], text[1], text[2]);

  // Tabla superior: 4 filas
  const gx = margin;               // grid left
  const gw = W - 2 * margin;       // grid width
  const rowH = 22;
  const cellLabelPad = 6;

  // Row 1: [Médico Tratante | valor | Fecha | valor]
  // Row 2: [Nombre del Paciente | valor | Cédula | valor]
  // Row 3: [Edad | valor | (empty span)]
  // Row 4: [Comentario | valor spanning]

  // We'll define column templates per row
  const drawCell = (x, yy, w, h, opts = {}) => {
    if (opts.fill) {
      doc.setFillColor(cellFill[0], cellFill[1], cellFill[2]);
      doc.rect(x, yy, w, h, "FD");
    } else {
      doc.rect(x, yy, w, h, "S");
    }
  };
  const drawLabel = (label, x, yy) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(text[0], text[1], text[2]);
    doc.text(label, x + cellLabelPad, yy + 14);
  };
  const drawValue = (value, x, yy, w) => {
    if (!value) return;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(text[0], text[1], text[2]);
    const lines = doc.splitTextToSize(String(value), w - cellLabelPad * 2);
    doc.text(lines, x + cellLabelPad, yy + 14);
  };

  // Row 1 — Médico Tratante | Fecha
  const lblW = 130;                     // ancho etiquetas
  const rightLabelW = 60;               // Fecha label width
  const midX = gx + gw / 2 + 60;        // separator between doctor col and date col
  const dateLabelX = midX;
  const dateValueX = midX + rightLabelW;

  // 1) Médico Tratante label
  drawCell(gx, y, lblW, rowH, { fill: true });
  drawLabel("Médico Tratante:", gx, y);
  // 1) Médico Tratante value
  drawCell(gx + lblW, y, midX - (gx + lblW), rowH);
  drawValue(rep.doctor_name || "", gx + lblW, y, midX - (gx + lblW));
  // 1) Fecha label
  drawCell(dateLabelX, y, rightLabelW, rowH, { fill: true });
  drawLabel("Fecha:", dateLabelX, y);
  // 1) Fecha value
  drawCell(dateValueX, y, gx + gw - dateValueX, rowH);
  const dateStr = rep.report_date
    ? new Date(rep.report_date).toLocaleDateString("es-ES")
    : new Date(rep.created_at || Date.now()).toLocaleDateString("es-ES");
  drawValue(dateStr, dateValueX, y, gx + gw - dateValueX);
  y += rowH;

  // Row 2 — Nombre del Paciente | Cédula
  drawCell(gx, y, lblW, rowH, { fill: true });
  drawLabel("Nombre del Paciente:", gx, y);
  drawCell(gx + lblW, y, midX - (gx + lblW), rowH);
  drawValue(rep.patient_name || "", gx + lblW, y, midX - (gx + lblW));
  drawCell(dateLabelX, y, rightLabelW, rowH, { fill: true });
  drawLabel("Cédula:", dateLabelX, y);
  drawCell(dateValueX, y, gx + gw - dateValueX, rowH);
  drawValue(rep.patient_nif || "", dateValueX, y, gx + gw - dateValueX);
  y += rowH;

  // Row 3 — Edad | (extended empty)
  const edadValW = 60;
  drawCell(gx, y, lblW, rowH, { fill: true });
  drawLabel("Edad:", gx, y);
  drawCell(gx + lblW, y, edadValW, rowH);
  drawValue(rep.patient_age ? String(rep.patient_age) : "", gx + lblW, y, edadValW);
  drawCell(gx + lblW + edadValW, y, gx + gw - (gx + lblW + edadValW), rowH);
  y += rowH;

  // Row 4 — Comentario spanning
  drawCell(gx, y, lblW, rowH, { fill: true });
  drawLabel("Comentario:", gx, y);
  drawCell(gx + lblW, y, gx + gw - (gx + lblW), rowH);
  drawValue(rep.comment || "", gx + lblW, y, gx + gw - (gx + lblW));
  y += rowH;

  // Vertical left border spanning full grid — already drawn by cell borders.
  y += 22;   // separación clara entre la tabla superior y las secciones

  // ---------- Cuerpo del informe ----------
  // Espaciado profesional recomendado para documentos clínicos:
  // — 16pt sobre el título · 10pt debajo · 14pt de interlineado en el cuerpo · 22pt entre secciones
  const drawSection = (title, content, minLines = 3) => {
    // Título de sección con filete tenue
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(15, 118, 110);
    doc.text(title, gx, y);
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.4);
    doc.line(gx, y + 4, gx + gw, y + 4);
    y += 18;   // espacio bajo el título antes del contenido

    // Cuerpo
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(text[0], text[1], text[2]);
    const val = (content || "").trim();
    if (val) {
      const lines = doc.splitTextToSize(val, gw);
      const needed = lines.length * 14 + 20;
      if (y + needed > H - 170) {
        doc.addPage();
        y = margin;
      }
      doc.text(lines, gx, y, { lineHeightFactor: 1.35 });
      y += lines.length * 14 + 22;   // 22pt de separación entre secciones
    } else {
      // Sin contenido — reserva mínima para escritura manual (impresión física)
      y += minLines * 14 + 20;
    }
  };

  drawSection("MOTIVO DE CONSULTA", rep.reason, 2);
  drawSection("ENFERMEDAD ACTUAL", rep.current_illness, 5);
  drawSection("IMPRESIÓN DIAGNÓSTICA", rep.diagnosis_impression, 2);
  drawSection("INDICACIONES", rep.indications, 4);

  // ---------- Pie de página (firma + eIDAS + QR) ----------
  // Deja al menos 24pt entre el contenido y el pie
  if (y > H - 160) {
    doc.addPage();
    y = margin;
  }
  const footerTop = y + 24;

  // Línea separadora
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.5);
  doc.line(margin, footerTop, W - margin, footerTop);

  // Bloque firma del profesional (centrado, como en el modelo)
  const sigX = W / 2;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(text[0], text[1], text[2]);
  // Firma manuscrita (imagen) si está disponible
  try {
    const sig = await loadSignatureDataUrl();
    if (sig) {
      doc.addImage(sig, "PNG", sigX - 60, footerTop + 10, 120, 40);
    }
  } catch (e) { /* ignore */ }
  const sigTextY = footerTop + 62;
  doc.setFont("helvetica", "bold");
  doc.text(`Dr. ${rep.doctor_name?.replace(/^Dr\.?\s*/i, "") || ""}`.trim(), sigX, sigTextY, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setTextColor(71, 85, 105);
  if (rep.doctor_specialty) {
    doc.text(rep.doctor_specialty, sigX, sigTextY + 12, { align: "center" });
  }
  if (rep.doctor_license) {
    doc.text(`Nº Colegiado: ${rep.doctor_license}`, sigX, sigTextY + 24, { align: "center" });
  }

  // eIDAS mini-bloque (izquierda)
  const sig = rep.signature || {};
  if (sig.hash) {
    doc.setFontSize(7.5);
    doc.setTextColor(15, 118, 110);
    doc.setFont("helvetica", "bold");
    doc.text("FIRMA ELECTRÓNICA SIMPLE (eIDAS)", margin, footerTop + 20);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(7);
    const signedAt = sig.signed_at ? new Date(sig.signed_at).toLocaleString("es-ES", { timeZone: "Europe/Madrid" }) : "-";
    doc.text(`Sellado: ${signedAt}`, margin, footerTop + 32);
    doc.text(`Algoritmo: ${sig.algo || "HMAC-SHA256"}`, margin, footerTop + 42);
    doc.setFont("courier", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(15, 23, 42);
    doc.text(`HASH ${sig.short || sig.hash.slice(0, 12).toUpperCase()}`, margin, footerTop + 54);
  }

  // QR verificación (derecha)
  try {
    const QRCode = (await import("qrcode")).default;
    const verifyUrl = `${window.location.origin}/verify/${rep.code}?v=${rep.verification_code || ""}`;
    const dataUrl = await QRCode.toDataURL(verifyUrl, { margin: 0, width: 220 });
    const qSize = 66;
    doc.addImage(dataUrl, "PNG", W - margin - qSize, footerTop + 10, qSize, qSize);
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text("Verificar informe", W - margin - qSize / 2, footerTop + qSize + 20, { align: "center" });
  } catch (e) { /* ignore */ }

  // Footer inferior (código + clínica)
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, H - 44, W - margin, H - 44);
  doc.setFontSize(7.5);
  doc.setTextColor(148, 163, 184);
  doc.text(
    `Informe: ${rep.code}  ·  ${settings.clinic_name || "Dr. Dimy Laguerre"}  ·  ${settings.clinic_email || "djimlag25@gmail.com"}  ·  ${settings.clinic_phone || "+34 742 08 65 96"}`,
    W / 2, H - 30, { align: "center" }
  );
  doc.text(
    "Documento generado electrónicamente · Verifique mediante el código QR.",
    W / 2, H - 20, { align: "center" }
  );

  const filename = `informe-${rep.code}.pdf`;
  if (opts.download !== false) {
    doc.save(filename);
  }
  // Return base64 (raw, without data: prefix) for email attachment use case
  const dataUri = doc.output("datauristring");
  const base64 = dataUri.split(",")[1] || "";
  return { filename, base64, doc };
}
